import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from './axios';

const BrandingContext = createContext(null);

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within BrandingProvider');
  }
  return context;
};

const defaultBranding = {
  logo_url: '',
  primary_color: '#1e40af',
  secondary_color: '#3b82f6',
  accent_color: '#60a5fa',
  favicon_url: '',
  login_background: '',
  footer_text: '© 2025 TechOrange-Legal. All rights reserved.',
  email_sender_name: 'TechOrange-Legal Opinion System',
  company_name: 'TechOrange-Legal',
  company_address: '',
  support_email: 'support@techorange-legal.com',
  support_phone: ''
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(defaultBranding);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Apply CSS variables to document
  const applyBranding = (brandingData) => {
    const root = document.documentElement;
    
    // Apply color variables
    root.style.setProperty('--primary-color', brandingData.primary_color || defaultBranding.primary_color);
    root.style.setProperty('--secondary-color', brandingData.secondary_color || defaultBranding.secondary_color);
    root.style.setProperty('--accent-color', brandingData.accent_color || defaultBranding.accent_color);
    
    // Apply favicon
    if (brandingData.favicon_url) {
      const favicon = document.querySelector("link[rel*='icon']") || document.createElement('link');
      favicon.type = 'image/x-icon';
      favicon.rel = 'shortcut icon';
      favicon.href = brandingData.favicon_url;
      document.getElementsByTagName('head')[0].appendChild(favicon);
    }
    
    // Update page title if company name is set
    if (brandingData.company_name) {
      document.title = `${brandingData.company_name} - Legal Opinion System`;
    }
  };

  // Fetch branding on mount
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        setLoading(true);
        
        // Detect domain from window.location
        const currentDomain = window.location.hostname;
        
        // Always try domain-based branding first (works without authentication)
        let response;
        let brandingLoaded = false;
        
        try {
          // Try to fetch branding by domain (public endpoint)
          response = await axios.get(`/api/branding/by-domain?domain=${currentDomain}`);
          console.log(`🎨 Loaded branding for domain: ${currentDomain}`);
          brandingLoaded = true;
        } catch (domainErr) {
          console.log('Domain branding not found, trying authenticated endpoint...');
          
          // Fallback: Try authenticated endpoint for logged-in users
          try {
            response = await axios.get('/api/admin/branding');
            console.log('🎨 Loaded branding from authenticated endpoint');
            brandingLoaded = true;
          } catch (authErr) {
            // If both fail, use default branding
            console.log('Using default branding (not logged in and no domain mapping)');
            setBranding(defaultBranding);
            applyBranding(defaultBranding);
            setLoading(false);
            return;
          }
        }
        
        if (!brandingLoaded) {
          setBranding(defaultBranding);
          applyBranding(defaultBranding);
          setLoading(false);
          return;
        }
        
        const brandingData = response.data;
        setBranding(brandingData);
        applyBranding(brandingData);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch branding:', err);
        // Use default branding on error
        setBranding(defaultBranding);
        applyBranding(defaultBranding);
        setError('Failed to load branding configuration');
      } finally {
        setLoading(false);
      }
    };

    fetchBranding();
  }, []);

  // Function to refresh branding (for admin panel)
  const refreshBranding = async () => {
    try {
      const response = await axios.get('/api/admin/branding');
      const brandingData = response.data;
      setBranding(brandingData);
      applyBranding(brandingData);
      return brandingData;
    } catch (err) {
      console.error('Failed to refresh branding:', err);
      throw err;
    }
  };

  // Function to update branding
  const updateBranding = async (brandingData) => {
    try {
      const response = await axios.put('/api/admin/branding', brandingData);
      const updatedBranding = response.data;
      setBranding(updatedBranding);
      applyBranding(updatedBranding);
      return updatedBranding;
    } catch (err) {
      console.error('Failed to update branding:', err);
      throw err;
    }
  };

  const value = {
    branding,
    loading,
    error,
    refreshBranding,
    updateBranding,
    defaultBranding
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;
